import { useState } from 'react';

import { EmptyState } from '@/components/feedback/EmptyState';
import {
  AlertCircleIcon,
  BriefcaseIcon,
  CalendarIcon,
  PlusIcon,
  SpinnerIcon,
} from '@/components/icons';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import { Modal } from '@/components/ui/Modal';
import { PageHeading, SectionHeading, Metric } from '@/components/portal/PortalPage';
import { useMissions } from '@/features/portal/usePortal';
import {
  useCreateLeave,
  useLeaveBalance,
  useMyLeaves,
  useTimesheetOptions,
} from '@/features/timesheets/useTimesheets';
import { formatDays } from '@/features/timesheets/timesheet-labels';

const STATUS_TONES: Record<string, 'warning' | 'success' | 'danger'> = {
  EN_ATTENTE: 'warning',
  ACCEPTEE: 'success',
  REFUSEE: 'danger',
};

/** Date au format français. Midi fixe : une date civile ne change pas de jour. */
const formatDay = (value: string): string =>
  new Date(`${value}T12:00:00`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/**
 * Demandes de congés du consultant.
 *
 * ── Pourquoi demander à l'avance plutôt que déclarer après coup ──────────
 * Une demande acceptée **remplit le CRA toute seule** sur les mois qu'elle
 * couvre. Déclarer des congés directement dans la grille marcherait aussi,
 * mais ne laisserait ni trace de l'accord, ni décompte des droits.
 *
 * Le solde est calculé par le serveur et jamais stocké : un solde en base
 * diverge au premier congé corrigé à la main.
 */
export const LeavesPage = () => {
  const [open, setOpen] = useState(false);
  const [missionId, setMissionId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [halfDayStart, setHalfDayStart] = useState(false);
  const [halfDayEnd, setHalfDayEnd] = useState(false);
  const [comment, setComment] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const leaves = useMyLeaves();
  const missions = useMissions();
  const options = useTimesheetOptions();
  const create = useCreateLeave();

  const active = (missions.data ?? []).filter((mission) => mission.isActive);
  const effectiveMissionId = missionId || (active.length === 1 ? (active[0]?.id ?? '') : '');
  const balance = useLeaveBalance(effectiveMissionId === '' ? undefined : effectiveMissionId);

  const missingMission = effectiveMissionId === '';
  const missingType = typeId === '';
  const missingDates = startDate === '' || endDate === '';
  const badOrder = startDate !== '' && endDate !== '' && endDate < startDate;
  const invalid = missingMission || missingType || missingDates || badOrder;

  const reset = (): void => {
    setOpen(false);
    setShowErrors(false);
    setStartDate('');
    setEndDate('');
    setHalfDayStart(false);
    setHalfDayEnd(false);
    setComment('');
  };

  return (
    <div className="portal-page">
      <PageHeading
        title="Mes congés"
        description="Consultez vos droits et suivez vos demandes d’absence."
        icon={<CalendarIcon />}
        action={
          active.length > 0 ? (
            <Button onClick={() => setOpen(true)}>
              <PlusIcon />
              Demander des congés
            </Button>
          ) : undefined
        }
      />
      {active.length > 1 && (
        <div className="max-w-sm">
          <Select
            label="Solde de la mission"
            placeholder="Choisissez une mission"
            options={active.map((mission) => ({
              value: mission.id,
              label: mission.clientName ?? mission.projectName,
            }))}
            value={missionId}
            onChange={(event) => setMissionId(event.target.value)}
          />
        </div>
      )}

      {/* ── Solde ───────────────────────────────────────────────────────── */}
      {balance.data ? (
        <dl className="portal-metrics portal-metrics--four">
          <Metric
            label="Droits ouverts"
            value={String(balance.data.entitlement.total)}
            hint="jours par an"
          />
          <Metric
            label="Pris"
            value={formatDays(balance.data.takenTotal)}
            hint="demandes acceptées"
          />
          <Metric
            label="En attente"
            value={formatDays(balance.data.pendingTotal)}
            hint="pas encore décidées"
          />
          <Metric
            accent
            label="Restant"
            value={formatDays(balance.data.remaining)}
            hint="droits moins jours pris"
          />
        </dl>
      ) : null}

      {/* ── Nouvelle demande ────────────────────────────────────────────── */}
      {open ? (
        <Modal
          title="Demander des congés"
          description="Une demande acceptée se reporte automatiquement dans votre CRA."
          icon={<CalendarIcon />}
          onClose={reset}
          closeDisabled={create.isPending}
          footer={
            <div className="border-navy-100 flex flex-wrap justify-end gap-2 border-t pt-4">
              <Button type="button" variant="secondary" disabled={create.isPending} onClick={reset}>
                Annuler
              </Button>
              <Button
                type="button"
                loading={create.isPending}
                loadingLabel="Envoi…"
                onClick={() => {
                  setShowErrors(true);
                  if (invalid) return;

                  create.mutate(
                    {
                      missionId: effectiveMissionId,
                      typeId,
                      startDate,
                      endDate,
                      halfDayStart,
                      halfDayEnd,
                      comment: comment.trim() === '' ? null : comment.trim(),
                    },
                    { onSuccess: reset },
                  );
                }}
              >
                Envoyer la demande
              </Button>
            </div>
          }
        >
          <div className="portal-form-fields">
            <div className="grid gap-4 sm:grid-cols-2">
              {active.length > 1 ? (
                <Select
                  label="Mission *"
                  placeholder="Choisissez une mission"
                  options={active.map((mission) => ({
                    value: mission.id,
                    label:
                      [mission.clientName, mission.title].filter(Boolean).join(' — ') ||
                      mission.projectName,
                  }))}
                  value={missionId}
                  error={showErrors && missingMission ? 'Choisissez la mission.' : undefined}
                  onChange={(event) => {
                    setMissionId(event.target.value);
                  }}
                />
              ) : null}

              <Select
                label="Type *"
                placeholder="Choisissez un type"
                options={(options.data?.leaveTypes ?? []).map((type) => ({
                  value: type.id,
                  label: type.label,
                }))}
                value={typeId}
                error={showErrors && missingType ? 'Choisissez le type de congé.' : undefined}
                onChange={(event) => {
                  setTypeId(event.target.value);
                }}
              />

              <div className="sm:col-span-2">
                <DatePicker
                  label="Période de congé *"
                  mode="flexible"
                  value={{ from: startDate, to: endDate }}
                  onValueChange={({ from, to }) => {
                    setStartDate(from);
                    setEndDate(to);
                  }}
                  error={
                    showErrors && missingDates
                      ? 'Choisissez une journée ou une période.'
                      : showErrors && badOrder
                        ? 'La fin doit suivre le début.'
                        : undefined
                  }
                />
              </div>

              {/* Partir un vendredi midi est courant : les demi-journées aux
                extrémités retirent 0,5 jour chacune du décompte. */}
              <Checkbox
                label="Je pars en milieu de première journée"
                checked={halfDayStart}
                onChange={(event) => {
                  setHalfDayStart(event.target.checked);
                }}
              />
              <Checkbox
                label="Je reviens en milieu de dernière journée"
                checked={halfDayEnd}
                onChange={(event) => {
                  setHalfDayEnd(event.target.checked);
                }}
              />
            </div>

            <Textarea
              label="Commentaire"
              rows={2}
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />

            {create.isError ? (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              >
                <AlertCircleIcon className="mt-0.5 shrink-0 text-base" />
                {create.error.message}
              </p>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {/* ── Historique ──────────────────────────────────────────────────── */}
      <section className="portal-surface">
        <SectionHeading
          title="Historique des demandes"
          description="Retrouvez vos périodes d’absence et leur statut."
        />
        {leaves.isPending ? (
          <p role="status" className="text-ink-500 flex items-center gap-2 px-5 py-6 text-sm">
            <SpinnerIcon className="text-base" />
            Chargement de vos demandes…
          </p>
        ) : leaves.isError ? (
          <div role="alert" className="px-5 py-6 text-sm text-red-800">
            {leaves.error.message}
          </div>
        ) : (leaves.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            className="my-8"
            icon={<BriefcaseIcon />}
            title="Aucune demande"
            description="Vos demandes de congés apparaîtront ici, avec la décision de ubeefy."
          />
        ) : (
          <ul className="portal-list">
            {(leaves.data?.items ?? []).map((leave) => (
              <li key={leave.id} className="portal-leave-row">
                <span className="portal-row-icon" aria-hidden>
                  <CalendarIcon />
                </span>
                <span className="min-w-0">
                  <strong className="text-navy-900 block text-sm font-medium">
                    {formatDay(leave.startDate)} → {formatDay(leave.endDate)}
                  </strong>
                  <span className="text-ink-500 block text-xs">{leave.type.label}</span>
                </span>

                <span>
                  <strong>{leave.days ? formatDays(leave.days) : '—'}</strong>
                  <small>jours demandés</small>
                </span>
                <span>
                  <Badge tone={STATUS_TONES[leave.status.code] ?? 'neutral'}>
                    {leave.status.label}
                  </Badge>
                  {leave.rejectionReason ? (
                    <span className="mt-0.5 block text-[11px] text-red-700">
                      {leave.rejectionReason}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
